import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, CheckCircle2, AlertCircle, ArrowRight, Database, ShieldCheck, Workflow, UserCheck, Server, Zap, RotateCcw } from 'lucide-react';
const steps = [{
  id: 1,
  title: 'Checks booking and room occupancy',
  icon: Database,
  type: 'integration',
  delay: 0.5
}, {
  id: 2,
  title: 'Reviews loyalty tier and service policy',
  icon: ShieldCheck,
  type: 'reasoning',
  delay: 1.5
}, {
  id: 3,
  title: 'Coordinates transport availability',
  icon: Workflow,
  type: 'workflow',
  delay: 2.5
}, {
  id: 4,
  title: 'Updates PMS / CRM / messaging system',
  icon: Server,
  type: 'integration',
  delay: 3.5
}, {
  id: 5,
  title: 'Confirms back to guest',
  icon: MessageSquare,
  type: 'action',
  delay: 4.5
}, {
  id: 6,
  title: 'Flags exceptions for human review if needed',
  icon: UserCheck,
  type: 'handoff',
  delay: 5.5
}];
export function AgentDemo() {
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  // Auto-play the demo when it comes into view
  const startDemo = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setIsComplete(false);
    setActiveStep(0);
    steps.forEach((step, index) => {
      setTimeout(() => {
        setActiveStep(index + 1);
        if (index === steps.length - 1) {
          setTimeout(() => {
            setIsProcessing(false);
            setIsComplete(true);
            setActiveStep(steps.length + 1); // Move past the last step
          }, 1500);
        }
      }, step.delay * 1000);
    });
  };
  return <section className="py-32 bg-[#050505] relative z-10 border-t border-white/5 overflow-hidden" data-id="element-37">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-38" />

      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-39">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-20" data-id="element-40">
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6 tracking-tight leading-[1.0] text-balance" data-id="element-41">
            Meet the Agent Layer.
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-42">
            More than just chat. Watch how Aijentik orchestrates reasoning,
            workflow execution, integrations, and human oversight in real-time.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} onViewportEnter={startDemo} transition={{
        duration: 1
      }} className="bg-[#0A0A0A] border border-white/10 rounded-3xl overflow-hidden shadow-2xl" data-id="element-43">
          {/* Header */}
          <div className="bg-[#111] border-b border-white/10 px-6 py-4 flex items-center justify-between" data-id="element-44">
            <div className="flex items-center gap-3" data-id="element-45">
              <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" data-id="element-46" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" data-id="element-47" />
              <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" data-id="element-48" />
            </div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-[0.25em]" data-id="element-49">
              Aijentik Execution Engine
            </div>
            <div className="w-16" data-id="element-50" /> {/* Spacer for balance */}
          </div>

          <div className="p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-51">
            {/* Input Panel */}
            <div className="lg:col-span-5 flex flex-col" data-id="element-52">
              <div className="mb-6" data-id="element-53">
                <div className="text-xs font-medium text-brand-orange uppercase tracking-[0.25em] mb-3 flex items-center gap-2" data-id="element-54">
                  <Zap className="w-3 h-3" data-id="element-55" />
                  Incoming Request
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative" data-id="element-56">
                  <div className="absolute -left-3 top-6 w-6 h-6 bg-brand-orange/20 rounded-full flex items-center justify-center border border-brand-orange/30" data-id="element-57">
                    <UserCheck className="w-3 h-3 text-brand-orange" data-id="element-58" />
                  </div>
                  <p className="text-gray-200 text-lg leading-relaxed font-medium" data-id="element-59">
                    "A VIP guest has requested a late check-out and airport
                    transfer. Resolve and update all relevant systems."
                  </p>
                </div>
              </div>

              <div className="flex-1 bg-gradient-to-b from-transparent to-white/[0.02] rounded-2xl border border-white/5 p-6 mt-auto relative overflow-hidden" data-id="element-60">
                {/* Completion Glow Background */}
                <AnimatePresence data-id="element-61">
                  {isComplete && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="absolute inset-0 bg-green-500/5 pointer-events-none" data-id="element-62" />}
                </AnimatePresence>

                <div className="flex items-center justify-between mb-4 relative z-10" data-id="element-63">
                  <span className="text-sm font-medium text-gray-400" data-id="element-64">
                    System Status
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full transition-colors duration-500 ${isProcessing ? 'bg-brand-orange/20 text-brand-orange border border-brand-orange/30' : isComplete ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'}`} data-id="element-65">
                    {isProcessing ? 'Processing...' : isComplete ? 'Complete' : 'Idle'}
                  </span>
                </div>
                <div className="space-y-3 relative z-10" data-id="element-66">
                  <div className="flex items-center justify-between text-sm" data-id="element-67">
                    <span className="text-gray-500" data-id="element-68">Reasoning Engine</span>
                    <span className={isComplete ? 'text-green-400' : 'text-gray-300'} data-id="element-69">
                      {isComplete ? 'Finished' : 'Active'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-70">
                    <span className="text-gray-500" data-id="element-71">Integrations</span>
                    <span className={isComplete ? 'text-green-400' : 'text-gray-300'} data-id="element-72">
                      {isComplete ? 'Synced (4)' : 'Connected (4)'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-73">
                    <span className="text-gray-500" data-id="element-74">Human Handoff</span>
                    <span className="text-gray-300" data-id="element-75">Not Required</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Execution Steps */}
            <div className="lg:col-span-7 relative flex flex-col" data-id="element-76">
              {/* Connecting Line */}
              <div className="absolute left-[23px] top-8 bottom-24 w-px bg-white/10" data-id="element-77" />

              <div className="space-y-6 relative flex-1" data-id="element-78">
                {steps.map((step, index) => {
                const isActive = activeStep === index + 1;
                const isCompleted = activeStep > index + 1;
                return <motion.div key={step.id} initial={{
                  opacity: 0.3
                }} animate={{
                  opacity: isActive || isCompleted ? 1 : 0.3,
                  x: isActive ? 10 : 0
                }} transition={{
                  duration: 0.4
                }} className="flex items-start gap-6 relative" data-id="element-79">
                      {/* Node */}
                      <div className={`relative z-10 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${isActive ? 'bg-brand-orange/20 border border-brand-orange/50 shadow-[0_0_15px_rgba(219,127,74,0.3)]' : isCompleted ? 'bg-white/10 border border-white/20' : 'bg-[#111] border border-white/5'}`} data-id="element-80">
                        {isCompleted ? <CheckCircle2 className="w-5 h-5 text-gray-400" data-id="element-81" /> : <step.icon className={`w-5 h-5 ${isActive ? 'text-brand-orange' : 'text-gray-600'}`} data-id="element-82" />}

                        {/* Active Pulse */}
                        {isActive && <motion.div className="absolute inset-0 rounded-xl border border-brand-orange" animate={{
                      scale: [1, 1.2, 1],
                      opacity: [1, 0, 1]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity
                    }} data-id="element-83" />}
                      </div>

                      {/* Content */}
                      <div className={`flex-1 pt-3 transition-colors duration-500 ${isActive ? 'text-white' : isCompleted ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-84">
                        <div className="flex items-center justify-between mb-1" data-id="element-85">
                          <h4 className="font-medium text-lg" data-id="element-86">{step.title}</h4>
                          <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${isActive ? 'border-brand-orange/30 text-brand-orange bg-brand-orange/10' : isCompleted ? 'border-white/10 text-gray-500 bg-white/5' : 'border-transparent text-gray-700'}`} data-id="element-87">
                            {step.type}
                          </span>
                        </div>

                        {/* Progress Bar for Active Step */}
                        <AnimatePresence data-id="element-88">
                          {isActive && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="mt-3 overflow-hidden" data-id="element-89">
                              <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-90">
                                <motion.div className="h-full bg-brand-orange" initial={{
                            width: '0%'
                          }} animate={{
                            width: '100%'
                          }} transition={{
                            duration: 1,
                            ease: 'linear'
                          }} data-id="element-91" />
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </motion.div>;
              })}
              </div>

              {/* Completion State & Replay Button */}
              <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between min-h-[60px]" data-id="element-92">
                <AnimatePresence data-id="element-93">
                  {isComplete && <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} className="flex items-center gap-3" data-id="element-94">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center" data-id="element-95">
                        <CheckCircle2 className="w-4 h-4 text-green-400" data-id="element-96" />
                      </div>
                      <span className="text-green-400 font-medium tracking-wide" data-id="element-97">
                        Action Completed — All systems updated
                      </span>
                    </motion.div>}
                </AnimatePresence>

                <AnimatePresence data-id="element-98">
                  {isComplete && <motion.button initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} onClick={startDemo} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-sm font-medium text-white transition-colors flex items-center gap-2" data-id="element-99">
                      <RotateCcw className="w-4 h-4" data-id="element-100" />
                      Replay Sequence
                    </motion.button>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}